import { useEffect, useMemo, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useTracking } from 'react-tracking';
import type { SearchResults as SearchResultsType } from '@/hooks/useSearch';
import { useSearch } from '@/hooks/useSearch';
import SearchOverlay from '../SearchOverlay';
import { SearchIcon } from '@/components/Nav/DeprecatedSearch';
import type { Theme } from '@simplywallst/ui-core';
import { useTheme } from '@simplywallst/ui-core';
import Media from 'react-media';
import DesktopSearch from '../DesktopSearch';
import { useServerEnv } from '@/hooks/useServerEnv';
import loadable from '@loadable/component';
import { useQuery } from '@tanstack/react-query';
import { queryKey, queryFn } from './query';
import type { ISO2 } from '@/constants/countries';
import { useDebouncedCallback } from 'use-debounce';

const SearchResult = loadable(
  () => import('@/components/Nav/DeprecatedSearch/components/SearchResult')
);

interface Listing {
  uniqueSymbol: string;
  exchangeCountryIso: ISO2;
  canonicalUrl: string;
}
interface Props {
  /** Input field name */
  name?: string;
  query?: string;
  prefix?: ReactNode;
  value?: string;
  placeholder?: string;
  activeIndex?: number;
  defaultState?: ReactNode;
  onSearchResult?: (results: SearchResultsType, query: string) => void;
  children?: (results: SearchResultsType) => ReactNode;
  onClose?: () => void;
  onReset?: () => void;
  onOpen?: (open: boolean, inputRef: RefObject<HTMLInputElement>) => void;
  onBlur?: (value: string) => void;
  primaryWrapper?: (
    child: ReactNode,
    props: {
      id: string;
      canonicalUrl: string;
      uniqueSymbol: string;
      name: string;
      listIndex: number;
    }
  ) => ReactNode;
  secondaryWrapper?: (child: ReactNode, listing: Listing) => ReactNode;
  theme?: Theme['appTheme'];
  error?: string;
}

const AlgoliaSearch = ({
  name,
  query,
  value,
  placeholder,
  activeIndex,
  defaultState,
  onSearchResult,
  onClose,
  onReset,
  onOpen,
  onBlur,
  primaryWrapper,
  secondaryWrapper,
  theme,
  error,
}: Props) => {
  const serverEnv = useServerEnv();
  const coretheme = useTheme();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'algolia',
    hasDefaultState: typeof defaultState !== 'undefined',
  });
  const [searchQuery, search] = useState(() =>
    typeof query !== 'undefined' && query !== '' ? query : ''
  );

  const debouncedOnChange = useDebouncedCallback(search, 500);

  const { data, status, isFetching } = useSearch(searchQuery, true);
  const userQuery = useQuery({
    queryKey: [queryKey],
    queryFn,
    staleTime: Infinity,
    enabled: false,
  });

  useEffect(() => {
    onSearchResult?.(data, searchQuery);
  }, [data]);

  /** Tracking */
  useEffect(() => {
    if (query !== '' && data.length > 0) {
      trackEvent({ action: 'query', value: query });
    }
  }, [query, data.length]);

  const fullMatchUniqueSymbol = useMemo(
    () =>
      data.reduce((acc, current) => {
        const { _highlightResult } = current;
        if (typeof _highlightResult.listings !== 'undefined') {
          const uniqueSymbol = _highlightResult.listings
            .filter((listing) => listing.uniqueSymbol.fullyHighlighted)
            .map((listing) => listing.uniqueSymbol.value.replace(/\s/g, ''));
          return acc.concat(...uniqueSymbol);
        }
        return acc;
      }, [] as string[]),
    [data]
  );

  const isShownDefaultState =
    searchQuery === '' || (data.length === 0 && !isFetching);
  return (
    <Track>
      <Media query={{ maxWidth: coretheme.mdMaxRaw }}>
        {(matches) => {
          if (serverEnv.isServer) return null;
          if (!userQuery.isFetched) {
            userQuery.refetch();
          }
          if (matches) {
            return (
              <SearchOverlay
                query={query}
                prefix={<SearchIcon />}
                onChange={debouncedOnChange}
                status={status}
                styleType={theme === 'light-v1' ? 'light' : theme}
                onClose={onClose}
                isFetching={isFetching}
                onReset={() => {
                  search('');
                  onReset?.();
                }}
              >
                {isShownDefaultState
                  ? defaultState
                  : data.map((result, i) => (
                      <li key={result.objectID}>
                        <SearchResult
                          light={theme === 'light'}
                          fullMatchUniqueSymbol={fullMatchUniqueSymbol}
                          id={result.objectID}
                          listIndex={i}
                          listings={result.listings}
                          name={result.name}
                          uniqueSymbol={result.uniqueSymbol}
                          canonicalUrl={result.url}
                          market={result.exchangeCountryIso}
                          tag={result.tag}
                          primaryWrapper={primaryWrapper}
                          secondaryWrapper={secondaryWrapper}
                          type={result.type}
                          description={result.description}
                          imageUrl={result.imageUrl}
                          website={result.website}
                          tickerSymbol={result.tickerSymbol}
                        />
                      </li>
                    ))}
              </SearchOverlay>
            );
          }
          /** assume desktop */
          return (
            <DesktopSearch
              value={value}
              light={theme === 'light'}
              name={name}
              onChange={search}
              status={status}
              isFetching={isFetching}
              onOpen={onOpen}
              placeholder={placeholder}
              onFocus={() => {
                SearchResult.preload();
                if (!userQuery.isFetched) {
                  userQuery.refetch();
                }
              }}
              onReset={onReset}
              defaultState={defaultState}
              error={error}
              onBlur={onBlur}
            >
              {data.map((result, i) => (
                <li key={result.objectID}>
                  <SearchResult
                    light={theme === 'light'}
                    fullMatchUniqueSymbol={fullMatchUniqueSymbol}
                    active={i === activeIndex}
                    id={result.objectID}
                    listIndex={i}
                    listings={result.listings}
                    name={result.name}
                    uniqueSymbol={result.uniqueSymbol}
                    canonicalUrl={result.url}
                    market={result.exchangeCountryIso}
                    tag={result.tag}
                    primaryWrapper={primaryWrapper}
                    secondaryWrapper={secondaryWrapper}
                    type={result.type}
                    description={result.description}
                    imageUrl={result.imageUrl}
                    website={result.website}
                    tickerSymbol={result.tickerSymbol}
                  />
                </li>
              ))}
            </DesktopSearch>
          );
        }}
      </Media>
    </Track>
  );
};

export default AlgoliaSearch;
